import { useSearchParams } from "react-router-dom";
import { Pencil, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { CrmRecordAuthUser } from "@/lib/api/crm/masters";
import { formatPhoneForDisplay } from "@/lib/validation/phoneNumber";
import { cn } from "@/lib/utils";
import { AUTH_USER_PARAM } from "../authUserForm";

type MobileLoginStatus = "active" | "disabled" | "banned";

const statusDetails: Record<MobileLoginStatus, { label: string; description: string; className: string }> = {
  active: {
    label: "Active",
    description: "Can log in to the mobile app.",
    className: "bg-green-50 text-green-700 ring-green-200",
  },
  disabled: {
    label: "Disabled",
    description: "Has an account, but it is disabled and can't log in.",
    className: "bg-amber-50 text-amber-700 ring-amber-200",
  },
  banned: {
    label: "Banned",
    description: "Has an account, but it is banned and can't log in.",
    className: "bg-red-50 text-red-700 ring-red-200",
  },
};

/** Banned wins over disabled; phone_number_verified is the account's enabled flag. */
function mobileLoginStatus(authUser: CrmRecordAuthUser): MobileLoginStatus {
  if (authUser.banned) return "banned";
  return authUser.phone_number_verified ? "active" : "disabled";
}

/** Mobile app accounts of the record's Primary and Mobile User phone numbers. */
export function MobileLoginList({ authUsers }: { authUsers: CrmRecordAuthUser[] }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const editingAuthUserId = searchParams.get(AUTH_USER_PARAM);

  if (authUsers.length === 0) {
    return (
      <p className="text-sm text-gray-400">
            None of this record's Primary or Mobile User phone numbers has a mobile app account.
      </p>
    );
  }

  return (
    <ul className="space-y-3">
      {authUsers.map((authUser) => {
        const status = statusDetails[mobileLoginStatus(authUser)];
        return (
          <li key={authUser.id} className="flex items-start gap-3" data-testid={`crm-mobile-login-${authUser.id}`}>
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-gray-500 ring-1 ring-gray-200">
              <Smartphone className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="truncate text-sm font-medium text-gray-900">
                  {authUser.phone_number ? formatPhoneForDisplay(authUser.phone_number) : "No phone number"}
                </span>
                <span
                  className={cn(
                    "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset",
                    status.className,
                  )}
                >
                  {status.label}
                </span>
              </div>
              <p className="text-xs text-gray-500">{status.description}</p>
              <p className="mt-1 text-xs text-gray-500">
                {authUser.organizations.length > 0
                  ? <>Organizations: <span className="text-gray-700">{authUser.organizations.map((organization) => organization.name).join(", ")}</span></>
                  : "Not in any organization."}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              disabled={editingAuthUserId === authUser.id}
              onClick={() => setSearchParams((previous) => {
                const next = new URLSearchParams(previous);
                next.set(AUTH_USER_PARAM, authUser.id);
                return next;
              })}
              data-testid={`crm-mobile-login-edit-${authUser.id}`}
            >
              <Pencil className="mr-1.5 h-3.5 w-3.5" />
              {editingAuthUserId === authUser.id ? "Permissions" : "Permissions"}
            </Button>
          </li>
        );
      })}
    </ul>
  );
}
